import React from "react";
import api from "../../../api/api"; // adjust relative path

const Step2Personal = ({ job, form, handleChange, prevStep, nextStep }) => {
  const [canadaDocs, setCanadaDocs] = React.useState([]);
  const [usDocs, setUsDocs] = React.useState([]);
  const isDriver = job ? [2, 8, 11].includes(Number(job.p_category)) : false;
  const CANADA_TASK_DOC_TYPE = "canada_documents";

  const fetchDocs = React.useCallback(async (region, docType, setter) => {
    try {
      const endpoint =
        region === "us"
          ? `/api/candidate/user/docs-us/${docType}`
          : region === "canadaTask"
            ? `/api/candidate/user/docs-canada/${docType}`
            : `/api/candidate/user/docs-canada/${docType}`;
      const { data } = await api.get(endpoint);
      setter(data);
    } catch (err) {
      console.error(`❌ Failed to fetch ${docType} docs:`, err);
    }
  }, []);

  React.useEffect(() => {
    if (form.dq_legal_right_work_us === "Yes" && form.us_doc_type) {
      fetchDocs("us", form.us_doc_type, setUsDocs);
    } else {
      setUsDocs([]);
    }

    if (form.dq_legal_right_enter_canada === "Yes") {
      fetchDocs("canadaTask", CANADA_TASK_DOC_TYPE, setCanadaDocs);
    } else {
      setCanadaDocs([]);
    }
  }, [
    fetchDocs,
    form.dq_legal_right_work_us,
    form.us_doc_type,
    form.dq_legal_right_enter_canada,
  ]);

  const handleUpload = async (region, docType, input, setter) => {
    if (!input) return;
    const fd = new FormData();

    const selectedFiles =
      input instanceof FileList ? Array.from(input) : Array.isArray(input) ? input : [input];

    for (const file of selectedFiles) {
      fd.append("files", file);
    }

    try {
      const uploadEndpoint =
        region === "us"
          ? `/api/candidate/user/upload-us/${docType}`
          : region === "canadaTask"
            ? `/api/candidate/user/upload-canada/${docType}`
          : `/api/candidate/user/upload-canada/${docType}`;

      await api.post(uploadEndpoint, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      fetchDocs(region, docType, setter);
    } catch (err) {
      console.error("❌ Upload failed:", err);
      alert("❌ Upload failed");
    }
  };

  const handleDelete = async (region, docType, docId, setter) => {
    if (!window.confirm("Delete this document?")) return;
    try {
      const endpoint =
        region === "us"
          ? `/api/candidate/user/docs-us/${docType}/${docId}`
          : region === "canadaTask"
            ? `/api/candidate/user/docs-canada/${docType}/${docId}`
          : `/api/candidate/user/docs-canada/${docType}/${docId}`;
      await api.delete(endpoint);
      fetchDocs(region, docType, setter);
    } catch (err) {
      console.error("❌ Delete failed:", err);
      alert("❌ Delete failed");
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (e.target.checkValidity()) {
      nextStep(); // only go forward if valid
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <fieldset className="border p-3 mb-4">
        <legend>Personal Information</legend>

        {/* Referral */}
        <div className="mb-3">
          <label className="d-block">
            Did someone refer you? <span className="text-danger">*</span>
          </label>
          <div className="btn-group w-100" role="group">
            <input
              type="radio"
              className="btn-check"
              name="refer_status"
              value="Yes"
              checked={form.refer_status === "Yes"}
              onChange={handleChange}
              id="refer_yes"
              required
            />
            <label className="btn btn-outline-primary px-5" htmlFor="refer_yes">
              Yes
            </label>

            <input
              type="radio"
              className="btn-check"
              name="refer_status"
              value="No"
              checked={form.refer_status === "No"}
              onChange={handleChange}
              id="refer_no"
            />
            <label
              className="btn btn-outline-secondary px-5"
              htmlFor="refer_no"
            >
              No
            </label>
          </div>

          {form.refer_status === "Yes" && (
            <div className="row mt-3">
              <div className="col-md-6">
                <input
                  type="text"
                  name="refer_by_name"
                  className="form-control mb-2"
                  placeholder="Employee Name"
                  value={form.refer_by_name}
                  onChange={handleChange}
                />
              </div>
              <div className="col-md-6">
                <input
                  type="text"
                  name="refer_by_id"
                  className="form-control mb-2"
                  placeholder="Employee ID"
                  value={form.refer_by_id}
                  onChange={handleChange}
                />
              </div>
            </div>
          )}
        </div>

        {job && isDriver && (
          <>
            {/* Legal right questions */}
            <div className="mb-3">
              <label className="d-block">
                Do you have the legal right to work in US?{" "}
                <span className="text-danger">*</span>
              </label>
              <div className="btn-group w-100" role="group">
                <input
                  type="radio"
                  className="btn-check"
                  name="dq_legal_right_work_us"
                  value="Yes"
                  checked={form.dq_legal_right_work_us === "Yes"}
                  onChange={handleChange}
                  id="canada_yes"
                  required
                />
                <label
                  className="btn btn-outline-primary px-5"
                  htmlFor="canada_yes"
                >
                  Yes
                </label>
                <input
                  type="radio"
                  className="btn-check"
                  name="dq_legal_right_work_us"
                  value="No"
                  checked={form.dq_legal_right_work_us === "No"}
                  onChange={handleChange}
                  id="canada_no"
                />
                <label
                  className="btn btn-outline-secondary px-5"
                  htmlFor="canada_no"
                >
                  No
                </label>
              </div>

              {form.dq_legal_right_work_us === "Yes" && (
                <div className="mt-2">
                  <label>Upload proof (US) (Optional)</label>
                  <div className="input-group mb-2 proof-upload-group">
                    <select
                      className="form-select"
                      value={form.us_doc_type || ""}
                      onChange={(e) =>
                        handleChange({
                          target: {
                            name: "us_doc_type",
                            value: e.target.value,
                          },
                        })
                      }
                    >
                      <option value="">Select document type</option>
                      <option value="work_permit">Work Permit</option>
                      <option value="green_card">Green Card</option>
                      <option value="passport">Passport</option>
                      <option value="birth_certificate">Birth Certificate</option>
                      <option value="citizenship_certificate">
                        Citizenship Certificate
                      </option>
                    </select>

                    <input
                      type="file"
                      multiple
                      accept=".pdf,.jpg,.jpeg,.png"
                      className="form-control"
                      onChange={(e) => {
                        if (!form.us_doc_type) {
                          alert("Please select a document type first");
                          return;
                        }

                        handleUpload(
                          "us",
                          form.us_doc_type,
                          e.target.files,
                          setUsDocs,
                        );
                      }}
                    />
                  </div>

                  {/* Uploaded files list */}
                  <ul className="list-group mt-2 proof-upload-list">
                    {usDocs.map((doc) => (
                      <li
                        key={doc.id}
                        className="list-group-item d-flex justify-content-between align-items-center"
                      >
                        <span className="proof-upload-name">
                          {doc.original}
                        </span>
                        <button
                          type="button"
                          className="btn btn-sm btn-danger"
                          onClick={() =>
                            handleDelete("us", form.us_doc_type, doc.id, setUsDocs)
                          }
                        >
                          🗑
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            <div className="mb-3">
              <label className="d-block">
                Do you have the legal right to enter Canada?{" "}
                <span className="text-danger">*</span>
              </label>
              <div className="btn-group w-100" role="group">
                <input
                  type="radio"
                  className="btn-check"
                  name="dq_legal_right_enter_canada"
                  value="Yes"
                  checked={form.dq_legal_right_enter_canada === "Yes"}
                  onChange={handleChange}
                  id="us_yes"
                  required
                />
                <label
                  className="btn btn-outline-primary px-5"
                  htmlFor="us_yes"
                >
                  Yes
                </label>
                <input
                  type="radio"
                  className="btn-check"
                  name="dq_legal_right_enter_canada"
                  value="No"
                  checked={form.dq_legal_right_enter_canada === "No"}
                  onChange={handleChange}
                  id="us_no"
                />
                <label
                  className="btn btn-outline-secondary px-5"
                  htmlFor="us_no"
                >
                  No
                </label>
              </div>

              {form.dq_legal_right_enter_canada === "Yes" && (
                <div className="mt-2">
                  <label>Upload proof (Canada) (Optional)</label>
                  <input
                    type="file"
                    multiple
                    accept=".pdf,.jpg,.jpeg,.png"
                    className="form-control"
                    onChange={(e) =>
                      handleUpload(
                        "canadaTask",
                        CANADA_TASK_DOC_TYPE,
                        e.target.files,
                        setCanadaDocs,
                      )
                    }
                  />

                  {/* Uploaded files list */}
                  <ul className="list-group mt-2 proof-upload-list">
                    {canadaDocs.map((doc) => (
                      <li
                        key={doc.id || doc.doc_id}
                        className="list-group-item d-flex justify-content-between align-items-center"
                      >
                        <span className="proof-upload-name">
                          {doc.original || doc.doc_original}
                        </span>
                        <button
                          type="button"
                          className="btn btn-sm btn-danger"
                          onClick={() =>
                            handleDelete(
                              "canadaTask",
                              CANADA_TASK_DOC_TYPE,
                              doc.id || doc.doc_id,
                              setCanadaDocs,
                            )
                          }
                        >
                          🗑
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            <div className="mb-3">
              <label className="d-block">
                Have you ever worked for New Way Transport Inc. before?{" "}
                <span className="text-danger">*</span>
              </label>
              <div className="btn-group w-100" role="group">
                <input
                  type="radio"
                  className="btn-check"
                  name="dq_previous_nwt_employee"
                  value="Yes"
                  checked={form.dq_previous_nwt_employee === "Yes"}
                  onChange={handleChange}
                  id="worked_yes"
                  required
                />
                <label
                  className="btn btn-outline-primary px-5"
                  htmlFor="worked_yes"
                >
                  Yes
                </label>
                <input
                  type="radio"
                  className="btn-check"
                  name="dq_previous_nwt_employee"
                  value="No"
                  checked={form.dq_previous_nwt_employee === "No"}
                  onChange={handleChange}
                  id="worked_no"
                />
                <label
                  className="btn btn-outline-secondary px-5"
                  htmlFor="worked_no"
                >
                  No
                </label>
              </div>

              {form.dq_previous_nwt_employee === "Yes" && (
                <div className="row mt-3">
                  <div className="col-md-6">
                    <label>
                      From Date <span className="text-danger">*</span>
                    </label>
                    <input
                      type="date"
                      name="dq_previous_nwt_from"
                      className="form-control"
                      value={form.dq_previous_nwt_from || ""}
                      onChange={handleChange}
                      required
                    />
                  </div>
                  <div className="col-md-6">
                    <label>
                      To Date <span className="text-danger">*</span>
                    </label>
                    <input
                      type="date"
                      name="dq_previous_nwt_to"
                      className="form-control"
                      value={form.dq_previous_nwt_to || ""}
                      onChange={handleChange}
                      required
                    />
                  </div>
                </div>
              )}
            </div>
          </>
        )}

        <div className="d-flex justify-content-between">
          <button className="btn btn-secondary" onClick={prevStep}>
            ← Back
          </button>
          <button type="submit" className="btn btn-primary">
            Next →
          </button>
        </div>
      </fieldset>
    </form>
  );
};

export default Step2Personal;
